import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosInstance } from 'axios';

import { ActionList } from '@xemplo/action-list';
import { Button, ButtonIconPosition, ButtonSize, ButtonType } from '@xemplo/button';
import { handleDownloadFile } from '@xemplo/common-util';
import { DownloadableFile } from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { DawnArrowDown } from '@xemplo/icons';
import { useDownloadableFiles } from '@xemplo/payrun-query';
import { createListItems, GroupBuilder } from '@xemplo/stateful-list-core';

export interface PayrunDownloadReportsProps {
  secondary?: boolean;
  payrunStatusId?: number | null;
}

function addDownloadItem(
  group: GroupBuilder,
  file: DownloadableFile,
  client: AxiosInstance,
) {
  group.addItem(file.id, `${file.type}.${file.extension}`, () =>
    handleDownloadFile(file, client),
  );
}

export function PayrunDownloadReports(props: Readonly<PayrunDownloadReportsProps>) {
  const { secondary = false, payrunStatusId = null } = props;
  const { client } = useHttpClient();
  const { id } = useParams();
  const files = useDownloadableFiles(id ?? null, payrunStatusId);

  const items = useMemo(() => {
    if (files.length === 0) return undefined;
    return createListItems()
      .addGroup((group) => {
        files.forEach((file) => addDownloadItem(group, file, client));
        return group;
      })
      .build();
  }, [files, client]);

  if (!items) return null;

  return (
    <ActionList isMulti={false} closeOnSelect listProps={{ items }}>
      <Button
        type={secondary ? ButtonType.Secondary : ButtonType.Primary}
        ariaLabel="download-reports-button"
        size={ButtonSize.Small}
        iconPosition={ButtonIconPosition.Trailing}
        icon={<DawnArrowDown fontSize={24} />}
      >
        Download
      </Button>
    </ActionList>
  );
}

export default PayrunDownloadReports;
